/**
 * Typography components, text inputs, and font constants for the PSI design
 * system.
 *
 * Font family constants are derived from the active theme and fall back to IBM
 * Plex Sans / IBM Plex Mono. `TextType` and `TextWeight` are the shared type
 * tokens used across all text components. `<UtilityText>` is the workhorse: it
 * renders translatable labels or plain strings with type, weight, colour,
 * underline, and caps options. All text components accept either `label`
 * (TLabel, translated) or `text` (raw string) — never both. `<TextField>` and
 * `<TextArea>` are controlled inputs with icon support, focus ring, and
 * error-state border.
 */

import { toHoverColor, useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { getFontLineHeight, getFontSize } from 'platform-specific/font';
import { WebLinkBase } from 'platform-specific/link';
import type { ComponentType, CSSProperties, Ref } from 'react';
import React, { useEffect, useState } from 'react';
import {
    type NativeSyntheticEvent,
    type StyleProp,
    StyleSheet,
    TextInput,
    type TextInputSubmitEditingEventData,
    type TextStyle,
    View,
} from 'react-native';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { makeTestID } from 'util/stdtypes';

import type { EllipsizeMode } from './translation';
import { TranslatableText, useTranslation } from './translation';

export type TextType = '2xl' | 'large' | 'small' | 'tiny';
export type TextWeight = 'regular' | 'medium' | 'strong';

export type HeadingProps = {
    text?: string;
    weight?: TextWeight;
    type?: TextType;
    color?: string;
    center?: boolean;
    label?: TLabel;
    numberOfLines?: number;
    underline?: boolean;
    formatParams?: FormatParams;
};

export function Heading({
    text,
    weight = 'strong',
    type = 'small',
    color,
    center = false,
    numberOfLines,
    label,
    underline = false,
    formatParams,
}: HeadingProps) {
    const { colors, fonts } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const weightMap = {
        regular: fonts.fontFamilySansRegular,
        medium: fonts.fontFamilySansMedium,
        strong: fonts.fontFamilySansSemiBold,
    };
    const sizeMap: Record<TextType, StyleProp<TextStyle>> = {
        '2xl': HeadingStyle['2xl'],
        large: HeadingStyle.large,
        small: HeadingStyle.small,
        tiny: HeadingStyle.tiny,
    };
    const ariaMap: Record<string, string> = {
        '2xl': '1',
        large: '1',
        small: '2',
        tiny: '2',
    };
    return (
        <TranslatableText
            aria-level={ariaMap[type]}
            formatParams={formatParams}
            label={label}
            numberOfLines={numberOfLines}
            role='heading'
            style={[
                { fontFamily: weightMap[weight] },
                sizeMap[type],
                underline && { textDecorationLine: 'underline' },
                center ? { textAlign: 'center' } : null,
                { color: resolvedColor },
            ]}
            text={text}
        />
    );
}

// HACK: React Native non-web doesn't support non-numeric line heights
const HeadingStyle = StyleSheet.create({
    '2xl': {
        fontSize: getFontSize(48),
        lineHeight: getFontLineHeight(48),
    },
    xlarge: {
        fontSize: getFontSize(36),
        lineHeight: getFontLineHeight(36),
    },
    large: {
        fontSize: getFontSize(24),
        lineHeight: getFontLineHeight(24),
    },
    small: {
        fontSize: getFontSize(16),
        lineHeight: getFontLineHeight(16),
    },
    tiny: {
        fontSize: getFontSize(12),
        lineHeight: getFontLineHeight(12),
    },
});
export type CardHeadingType = 'large' | 'small';

export type CardHeadingProps = {
    text?: string;
    type?: CardHeadingType;
    color?: string;
    center?: boolean;
    label?: TLabel;
    underline?: boolean;
    formatParams?: FormatParams;
};

export function CardHeading({ text, type = 'large', color, center = false, label, underline = false, formatParams }: CardHeadingProps) {
    const { colors, fonts } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const sizeMap = {
        large: CardHeadingStyle.large,
        small: CardHeadingStyle.small,
    };
    const ariaMap = {
        large: '1',
        small: '2',
    };
    return (
        <TranslatableText
            aria-level={ariaMap[type]}
            formatParams={formatParams}
            label={label}
            role='heading'
            style={[
                { fontFamily: fonts.fontFamilySansMedium },
                sizeMap[type],
                underline && { textDecorationLine: 'underline' },
                center ? { textAlign: 'center' } : null,
                { color: resolvedColor },
            ]}
            text={text}
        />
    );
}

const CardHeadingStyle = StyleSheet.create({
    large: {
        fontSize: getFontSize(28),
        lineHeight: getFontLineHeight(28),
    },
    small: {
        fontSize: getFontSize(18),
        lineHeight: getFontLineHeight(18),
    },
});

export type DataVizTextProps = {
    type?: 'large' | 'small' | 'tiny';
    text?: string;
    label?: TLabel;
    formatParams?: FormatParams;
    color?: string;
};

export function DataVizText({ type = 'large', text, label, formatParams, color }: DataVizTextProps) {
    const { colors } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const s = useDataVizTextStyle();
    const styleMap = {
        large: s.large,
        small: s.small,
        tiny: s.small,
    };
    return (
        <TranslatableText
            formatParams={formatParams}
            label={label}
            role='heading'
            style={[styleMap[type], { color: resolvedColor }]}
            text={text}
        />
    );
}

const useDataVizTextStyle = createThemedStyles((tokens) => ({
    large: {
        fontFamily: tokens.fonts.fontFamilySansSemiBold,
        fontSize: getFontSize(18),
        lineHeight: getFontLineHeight(18),
    },
    small: {
        fontFamily: tokens.fonts.fontFamilyMonoRegular,
        fontSize: getFontSize(12),
        lineHeight: getFontLineHeight(12),
    },
}));

export type DEPRECATED_EditorialHeadingProps = {
    type?: TextType;
    text?: string;
    label?: TLabel;
    underline?: boolean;
    formatParams?: FormatParams;
    color?: string;
    italic?: boolean;
};

export function DEPRECATED_EditorialHeading({
    type = 'large',
    text,
    label,
    underline,
    formatParams,
    color,
    italic = false,
}: DEPRECATED_EditorialHeadingProps) {
    const { colors } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const s = useDEPRECATED_EditorialHeadingStyle();
    const styleMap: Record<TextType, StyleProp<TextStyle>> = {
        '2xl': s.large,
        large: s.large,
        small: s.small,
        tiny: s.small,
    };
    return (
        <TranslatableText
            formatParams={formatParams}
            label={label}
            style={[
                styleMap[type],
                { color: resolvedColor },
                italic && { fontStyle: 'italic' },
                underline && { textDecorationLine: 'underline' },
            ]}
            text={text}
        />
    );
}

const useDEPRECATED_EditorialHeadingStyle = createThemedStyles((tokens) => ({
    large: {
        fontFamily: tokens.fonts.fontFamilySansRegular,
        fontSize: getFontSize(24),
    },
    small: {
        fontFamily: tokens.fonts.fontFamilySansRegular,
        fontSize: getFontSize(18),
    },
}));

export type ParagraphProps = {
    numberOfLines?: number;
    type?: TextType;
    color?: string;
    strong?: boolean;
    center?: boolean;
    text?: string;
    label?: TLabel;
    formatParams?: FormatParams;
    underline?: boolean;
    lineHeight?: number;
};

const paragraphFontSizeForType: Record<TextType, number> = {
    '2xl': 16,
    large: 16,
    small: 14,
    tiny: 14,
};

export function Paragraph({
    numberOfLines,
    type = 'small',
    color,
    strong = false,
    center = false,
    text,
    label,
    formatParams,
    underline = false,
    lineHeight,
}: ParagraphProps) {
    const { colors, fonts } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const s = useParagraphStyle();
    const styleMap: Record<TextType, StyleProp<TextStyle>> = {
        '2xl': s.largeParagraph,
        large: s.largeParagraph,
        small: s.smallParagraph,
        tiny: s.smallParagraph,
    };
    return (
        <TranslatableText
            formatParams={formatParams}
            label={label}
            numberOfLines={numberOfLines}
            role='paragraph'
            style={[
                styleMap[type],
                { color: resolvedColor },
                lineHeight != null && { lineHeight: getFontLineHeight(paragraphFontSizeForType[type], lineHeight) },
                strong && { fontFamily: fonts.fontFamilySansSemiBold },
                underline && { textDecorationLine: 'underline' },
                center && { textAlign: 'center' },
            ]}
            text={text}
        />
    );
}

/** @public */
const useParagraphStyle = createThemedStyles((tokens) => ({
    largeParagraph: {
        fontSize: getFontSize(16),
        lineHeight: getFontLineHeight(16, 1.5),
        fontFamily: tokens.fonts.fontFamilySansRegular,
    },
    smallParagraph: {
        fontSize: getFontSize(14),
        lineHeight: getFontLineHeight(14, 1.5),
        fontFamily: tokens.fonts.fontFamilySansRegular,
    },
}));

export type UtilityTextProps = {
    type?: TextType;
    center?: boolean;
    right?: boolean;
    text?: string;
    label?: TLabel;
    formatParams?: FormatParams;
    color?: string;
    weight?: TextWeight;
    caps?: boolean;
    underline?: boolean;
    numberOfLines?: number;
    ellipsizeMode?: EllipsizeMode;
    italic?: boolean;
    mono?: boolean;
    textRef?: React.RefObject<any>;
} & { [key: string]: any };

export function UtilityText({
    type = 'small',
    center = false,
    right = false,
    text,
    label,
    formatParams,
    color,
    weight = 'regular',
    caps = false,
    underline = false,
    numberOfLines,
    ellipsizeMode = 'tail',
    italic = false,
    mono = false,
    textRef,
    ...props
}: UtilityTextProps) {
    const { colors, fonts } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const s = UtilityTextStyle;
    const styleMap: Record<TextType, StyleProp<TextStyle>> = {
        '2xl': s.utilityLarge,
        large: s.utilityLarge,
        small: s.utilitySmall,
        tiny: s.utilityTiny,
    };
    const weightMap = {
        regular: fonts.fontFamilySansRegular,
        medium: fonts.fontFamilySansMedium,
        strong: fonts.fontFamilySansSemiBold,
    };
    const monoWeightMap = {
        regular: fonts.fontFamilyMonoRegular,
        medium: fonts.fontFamilyMonoMedium,
        strong: fonts.fontFamilyMonoSemiBold,
    };
    if (!text && !label) return null;
    return (
        <TranslatableText
            ellipsizeMode={ellipsizeMode}
            formatParams={formatParams}
            label={label}
            numberOfLines={numberOfLines}
            role='paragraph'
            style={[
                styleMap[type],
                { color: resolvedColor },
                italic && { fontStyle: 'italic' },
                underline && { textDecorationLine: 'underline' },
                weight && { fontFamily: mono ? monoWeightMap[weight] : weightMap[weight] },
                center && { textAlign: 'center' },
                right && { textAlign: 'right' },
                caps && { textTransform: 'uppercase', letterSpacing: 0.5 },
            ]}
            text={text}
            textRef={textRef}
            {...props}
        />
    );
}

const UtilityTextStyle = StyleSheet.create({
    utilityLarge: {
        fontSize: getFontSize(16),
        lineHeight: getFontLineHeight(16),
    },
    utilitySmall: {
        fontSize: getFontSize(14),
        lineHeight: getFontLineHeight(14),
    },
    utilityTiny: {
        fontSize: getFontSize(12),
        lineHeight: getFontLineHeight(12),
    },
});

export type WebLinkProps = {
    url: string;
    style?: CSSProperties;
    setHover?: (hovered: boolean) => void;
    testID?: string;
    children: React.ReactNode;
    onLinkPress?: () => void;
    linkType?: string;
};

export function WebLink({ url, style, setHover, testID, children, onLinkPress, linkType }: WebLinkProps) {
    const linkStyle = Object.assign({ position: 'relative' }, typeof style === 'object' ? style : {});

    return (
        <WebLinkBase linkType={linkType} onLinkPress={onLinkPress} setHover={setHover} style={linkStyle} testID={testID} url={url}>
            {children}
        </WebLinkBase>
    );
}

export type LinkTextProps = {
    type?: TextType;
    testID?: string;
    color?: string;
    text?: string;
    url?: string;
    label?: TLabel;
    formatParams?: FormatParams;
    weight?: TextWeight;
};

export function LinkText({ type = 'small' as TextType, testID, color, text, url, label, formatParams, weight }: LinkTextProps) {
    const { colors } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    if (!url) return null;
    return (
        <WebLink testID={testID} url={url}>
            <UtilityText
                color={resolvedColor}
                formatParams={formatParams}
                label={label}
                text={text}
                type={type}
                underline
                weight={weight}
            />
        </WebLink>
    );
}

export type WebLinkTextButtonProps = {
    label?: TLabel;
    heading?: boolean;
    text?: string;
    color?: string;
    url?: string;
    type?: TextType;
    mono?: boolean;
    weight?: TextWeight;
    numberOfLines?: number;
    onLinkPress?: () => void;
    linkType?: string;
};

export function WebLinkTextButton({
    label,
    heading,
    text,
    color,
    url,
    type = 'small',
    mono = false,
    weight = 'regular',
    numberOfLines,
    onLinkPress,
    linkType,
}: WebLinkTextButtonProps) {
    const { colors } = useTheme();
    const resolvedColor = color ?? colors.colorTextPrimary;
    const s = WebLinkTextButtonStyle;
    const [hover, setHover] = useState(false);
    if (!url) return null;
    return (
        <WebLink
            linkType={linkType}
            onLinkPress={onLinkPress}
            setHover={setHover}
            style={{ ...(hover ? s.hoverButton : s.button), color: resolvedColor }}
            testID={makeTestID({ label, text })}
            url={url}
        >
            {heading ? (
                <Heading color={resolvedColor} label={label} numberOfLines={numberOfLines} text={text} type={type} weight={weight} />
            ) : (
                <UtilityText
                    color={resolvedColor}
                    label={label}
                    mono={mono}
                    numberOfLines={numberOfLines}
                    text={text}
                    type={type}
                    weight={weight}
                />
            )}
        </WebLink>
    );
}
const WebLinkTextButtonStyle = StyleSheet.create({
    button: {
        textDecorationLine: 'none',
        overflow: 'hidden',
        ...({ textOverflow: 'ellipsis' } as any),
    },
    hoverButton: {
        textDecorationLine: 'none',
        overflow: 'hidden',
        opacity: 0.7,
        ...({ textOverflow: 'ellipsis' } as any),
    },
});

type BaseTextFieldProps = {
    ariaLabel?: TLabel;
    autoFocus?: boolean;
    editable?: boolean;
    error?: boolean;
    hoverStyle?: Record<string, any>;
    inputForwardedRef?: Ref<TextInput>;
    multiline?: boolean;
    placeholderText?: string;
    onBlur?: () => void;
    onChange?: (text: string) => void;
    onFocus?: () => void;
    onFocusChange?: (focused: boolean) => void;
    onSubmit?: (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => void;
    placeholderLabel?: TLabel;
    placeholderParams?: Record<string, unknown>;
    testID?: string;
    value?: string | null;
};

type AutoSizeTextInputProps = BaseTextFieldProps & {
    emptyHeight?: number;
    maxHeight?: number;
    placeholderTextColor?: string;
    style: Record<string, any>; // should eventually be StyleProp<TextStyle> ?
};

export function AutoSizeTextInput({
    ariaLabel,
    autoFocus,
    editable = true,
    emptyHeight = 48,
    hoverStyle,
    inputForwardedRef,
    maxHeight = 400,
    multiline = true,
    placeholderText,
    onBlur,
    onChange,
    onFocus,
    onSubmit,
    placeholderLabel,
    placeholderParams,
    placeholderTextColor,
    style,
    testID,
    value,
    ...props
}: AutoSizeTextInputProps) {
    const { colors } = useTheme();
    const resolvedPlaceholderTextColor = placeholderTextColor ?? colors.colorTextHint;
    const tPlaceholder = useTranslation(placeholderLabel, placeholderParams);
    const tAriaLabel = useTranslation(ariaLabel);
    const [height, setHeight] = useState(0);
    const [hover, setHover] = useState(false);

    useEffect(() => {
        if (value === '' && height > 0) {
            setHeight(emptyHeight);
        }
    }, [value, height, emptyHeight]);

    function onContentSizeChange(e: any) {
        const newHeight = e.nativeEvent.contentSize.height;
        if (newHeight > height) {
            if (value === '') return;
            setHeight(Math.min(newHeight, maxHeight));
        }
    }

    const styleHeight = Math.max(emptyHeight, height);

    // HACK: Mobile doesn't support onMouseEnter/onMouseLeave, so we use
    // onMouseEnter/onMouseLeave as any
    const platformSpecificAttrs = {
        onMouseEnter: () => setHover(true),
        onMouseLeave: () => setHover(false),
        disabled: !editable,
    } as any;

    return (
        <View style={{ height: styleHeight }}>
            <TextInput
                accessibilityLabel={tAriaLabel}
                autoFocus={autoFocus}
                editable={editable}
                multiline={multiline}
                onBlur={onBlur}
                onChangeText={onChange}
                onContentSizeChange={onContentSizeChange}
                onFocus={onFocus}
                onSubmitEditing={onSubmit}
                placeholder={placeholderText || tPlaceholder}
                placeholderTextColor={resolvedPlaceholderTextColor}
                ref={inputForwardedRef}
                style={[style, { height: styleHeight }, hover ? hoverStyle : null]}
                testID={testID}
                value={value}
                {...platformSpecificAttrs}
                {...props}
            />
        </View>
    );
}

export type TextFieldSize = 'small' | 'large';

export type TextFieldProps = BaseTextFieldProps & {
    icon?: ComponentType<any> | null;
    multiline?: boolean;
    size?: TextFieldSize;
};

export function TextField({
    value = null,
    icon,
    error = false,
    autoFocus = false,
    placeholderText,
    placeholderLabel: placeholder,
    testID,
    placeholderParams,
    onChange,
    onFocusChange,
    onSubmit,
    multiline = false,
    ariaLabel,
    size = 'large' as TextFieldSize,
    editable,
    inputForwardedRef,
    ...props
}: TextFieldProps) {
    const { colors } = useTheme();
    const s = useTextFieldStyles();
    const isSmall = size === 'small';
    const emptyHeight = isSmall ? 40 : 48;
    const multilinePadding = multiline ? { paddingVertical: 12 } : null;

    const commonStyles = [
        s.textField,
        error && { borderColor: colors.colorBorderWarning },
        editable === false && {
            backgroundColor: colors.colorSurfaceSecondary,
            color: colors.colorTextDisabled,
        },
    ];

    const styles = [...commonStyles, icon && { paddingLeft: isSmall ? 42 : 48 }, isSmall && { height: 40 }, multilinePadding];

    return (
        <View>
            <View>
                <AutoSizeTextInput
                    aria-invalid={error || undefined}
                    autoFocus={autoFocus}
                    editable={editable}
                    emptyHeight={emptyHeight}
                    hoverStyle={s.hover}
                    inputForwardedRef={inputForwardedRef}
                    multiline={multiline}
                    onBlur={() => onFocusChange?.(false)}
                    onChange={onChange ?? (() => {})}
                    onFocus={() => onFocusChange?.(true)}
                    onSubmit={onSubmit}
                    placeholderLabel={placeholder}
                    placeholderParams={placeholderParams}
                    placeholderText={placeholderText}
                    style={styles}
                    testID={testID}
                    value={value ?? ''}
                    {...props}
                />
                {icon &&
                    React.createElement(icon, {
                        size: 24,
                        color: colors.colorTextPrimary,
                        style: {
                            position: 'absolute',
                            top: isSmall ? 10 : 14,
                            left: 16,
                            zIndex: 100,
                            ...(isSmall ? { width: 20, height: 20 } : null),
                        },
                    })}
            </View>
        </View>
    );
}

const useTextFieldStyles = createThemedStyles((tokens) => ({
    textField: {
        outlineOffset: 3,
        borderColor: tokens.colors.colorBorderPrimary,
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderWidth: 1,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 12,
        fontSize: getFontSize(16),
        lineHeight: getFontLineHeight(16, 1.5),
        fontFamily: tokens.fonts.fontFamilySansRegular,
        color: tokens.colors.colorTextPrimary,
    },
    hover: {
        borderColor: toHoverColor(tokens.colors.colorBorderSelected),
    },
}));

type BaseTextAreaProps = BaseTextFieldProps;

export function TextArea(props: BaseTextAreaProps) {
    const { colors } = useTheme();
    const s = useTextFieldStyles();
    const editable = props.editable ?? true;
    const styles = [
        s.textField,
        props.error && { borderColor: colors.colorBorderWarning },
        !editable && {
            backgroundColor: colors.colorSurfaceSecondary,
            color: colors.colorTextDisabled,
        },
        { paddingVertical: 12 },
    ];
    const emptyHeight = 192;

    return (
        <AutoSizeTextInput
            aria-invalid={props.error || undefined}
            ariaLabel={props.ariaLabel}
            autoFocus={props.autoFocus}
            editable={props.editable ?? true}
            emptyHeight={emptyHeight}
            hoverStyle={s.hover}
            multiline
            onBlur={() => props.onFocusChange?.(false)}
            onChange={props.onChange}
            onFocus={() => props.onFocusChange?.(true)}
            onSubmit={props.onSubmit}
            placeholderLabel={props.placeholderLabel}
            placeholderParams={props.placeholderParams}
            placeholderText={props.placeholderText}
            style={styles}
            testID={props.testID}
            value={props.value ?? ''}
        />
    );
}

export function checkValidLength({ text, min, max }: { text: string; min: number; max: number }): boolean {
    const count = text?.length ?? 0;
    return count >= min && count <= max;
}

export type CircleCountProps = { count: number };

export function CircleCount({ count }: CircleCountProps) {
    const s = useCircleCountStyles();
    return (
        <View style={s.circle}>
            <UtilityText text={String(count)} weight='medium' />
        </View>
    );
}
const useCircleCountStyles = createThemedStyles((tokens) => ({
    circle: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderRadius: 100,
        width: 20,
        height: 20,
        justifyContent: 'center',
        alignItems: 'center',
    },
}));
